import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import DashboardHeader from "@components/DashboardHeader";
import Traductor from "@utils/traductor";
import { LANGUAGE, CORPORATION } from "@/constants";
import Typography from "@mui/material/Typography";
import ArticleIcon from "@mui/icons-material/Article";
import { useGlobalContext, useGlobalContextUpdate } from "@contexts/Global";
import theme from "@/theme";
const traductor = new Traductor(LANGUAGE);

const getColorByIcon = (name = "") => {
  const {
    main = "",
    secondary = "",
    third = "",
    fourth = "",
  } = theme.palette.dashbaord;
  if (name === "e14" || name === "switch") {
    return main;
  }
  if (name === "votes") {
    return fourth;
  }
  if (name === "claim") {
    return third;
  }
  if (name === "register") {
    return secondary;
  }
};

const getOptionIcon = (name = "") => {
  const styles = {
    color: getColorByIcon(name),
    fontSize: "60px",
    position: "relative",
    top: "25px",
    marginTop: "15px",
  };
  if (name === "e14") {
    return <CameraAltIcon sx={styles} />;
  }
  if (name === "votes") {
    return <HowToVoteIcon sx={styles} />;
  }
  if (name === "claim") {
    return <PlagiarismIcon sx={styles} />;
  }

  if (name === "register") {
    return <ArticleIcon sx={styles} />;
  }
};

const resolveCheckOption = ({
  name = "",
  corporation = "",
  currentTable = {},
}) => {
  const isSenado = CORPORATION.SENADO === corporation;
  const isCamara = CORPORATION.CAMARA === corporation;
  const isConsulta = CORPORATION.CONSULTA === corporation;
  const isE14 = name === "e14";
  const isVotes = name === "votes";
  const isClaim = name === "claim";
  const isRegister = name === "register";
  if (isSenado) {
    if (isE14) {
      return currentTable.E14_sen;
    }
    if (isVotes) {
      return currentTable.cuenta_votos_sen;
    }
    if (isClaim) {
      return currentTable.reclamaciones_sen;
    }
    if (isRegister) {
      return currentTable.ingreso_datos_sen;
    }
  }
  if (isCamara) {
    if (isE14) {
      return currentTable.E14_cam;
    }
    if (isVotes) {
      return currentTable.cuenta_votos_cam;
    }
    if (isClaim) {
      return currentTable.reclamaciones_cam;
    }
    if (isRegister) {
      return currentTable.ingreso_datos_cam;
    }
  }
  if (isConsulta) {
    if (isE14) {
      return currentTable.E14_consulta;
    }
    if (isVotes) {
      return currentTable.cuenta_votos_consulta;
    }
    if (isClaim) {
      return currentTable.reclamaciones_consulta;
    }
    if (isRegister) {
      return currentTable.ingreso_datos_consulta;
    }
  }
};

const Option = ({ children, name }) => {
  const { check = "" } = theme.palette.dashbaord;
  const color = getColorByIcon(name);
  const setGlobalContext = useGlobalContextUpdate();
  const globalContext = useGlobalContext();
  const { corporation, data = {} } = globalContext;
  const { currentTable = {} } = data;
  const isCheck = resolveCheckOption({ name, corporation, currentTable });

  const handleClick = () => {
    setGlobalContext({
      modalContent: name,
      isModalOpen: true,
      modalColor: getColorByIcon(name),
    });
  };
  return (
    <Card
      onClick={handleClick}
      variant="outlined"
      sx={{
        textAlign: "center",
        borderRadius: "10px",
        borderColor: color,
        borderWidth: "2px",
        minWidth: "180px",
        height: "210px",
        cursor: "pointer",
        transition: `all 0.35s ${theme.easing.easeInOut}`,
        "& svg": {
          transition: `all 0.35s ${theme.easing.easeInOut}`,
        },
        "&:hover": {
          backgroundColor: color,
          transform: "scale(1.025)",
        },
        "&:hover svg": {
          color: "white",
          transform: "scale(1.2)",
        },
        "&:hover .MuiTypography-root": {
          color: "rgba(255, 255, 255, 0.8)",
        },
      }}
    >
      <CardContent sx={{ position: "relative", cursor: "pointer" }}>
        {getOptionIcon(name)}
        {children}
        {isCheck && (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              color: check,
              left: "5px",
              top: "5px",
              fontSize: "30px",
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

const cardNameStyle = {
  fontWeight: "bold",
  fontSize: "1em",
  paddingTop: "20px",
  paddingBottom: "10px",
  marginTop: "20px",
  lineHeight: 1.2,
};

const E14 = ({ isCheck }) => (
  <Option name="e14" isCheck={isCheck}>
    <Typography
      sx={{ ...cardNameStyle, color: getColorByIcon("e14") }}
      variant="button"
      display="block"
      gutterBottom
    >
      {traductor.translate("e14")}
    </Typography>
  </Option>
);
const Votes = ({ isCheck }) => (
  <Option name="votes" isCheck={isCheck}>
    <Typography
      sx={{ ...cardNameStyle, color: getColorByIcon("votes") }}
      variant="button"
      display="block"
      gutterBottom
    >
      {traductor.translate("votes")}
    </Typography>
  </Option>
);

const Claim = ({ isCheck }) => (
  <Option name="claim" isCheck={isCheck}>
    <Typography
      sx={{ ...cardNameStyle, color: getColorByIcon("claim") }}
      variant="button"
      display="block"
      gutterBottom
    >
      {traductor.translate("claim")}
    </Typography>
  </Option>
);

const Register = ({ isCheck }) => (
  <Option name="register" isCheck={isCheck}>
    <Typography
      sx={{ ...cardNameStyle, color: getColorByIcon("register") }}
      variant="button"
      display="block"
      gutterBottom
    >
      {traductor.translate("register")}
    </Typography>
  </Option>
);

export const Dashboard = () => {
  const { data = {} } = useGlobalContext();
  const setGlobalContext = useGlobalContextUpdate();
  const { name, place, table, hasMultiplesTables } = data;

  const handleCorporationChange = (corporation) => {
    setGlobalContext({ corporation });
  };

  const handleSwitchTable = () => {
    setGlobalContext({
      modalContent: "switch",
      isModalOpen: true,
      modalColor: getColorByIcon("switch"),
    });
  };

  return (
    <Box
      sx={{
        margin: "0px auto",
        maxWidth: 800,
        position: "relative",
        paddingLeft: "5px",
        paddingRight: "5px",
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="flex-center"
        alignItems="center"
      >
        <Grid container>
          <Grid item md={12} sm={12} xs={12}>
            <DashboardHeader
              name={name}
              place={place}
              table={table}
              corporation={CORPORATION.SENADO}
              hasMultiplesTables={hasMultiplesTables}
              onCorporationChange={handleCorporationChange}
              onSwitchTable={handleSwitchTable}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item md={6} sm={6} xs={6}>
            <E14 />
          </Grid>

          <Grid item md={6} sm={6} xs={6}>
            <Votes />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item md={6} sm={6} xs={6}>
            <Register />
          </Grid>

          <Grid item md={6} sm={6} xs={6}>
            <Claim />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
